import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SchoolRosterStudent, UpdateSchoolRosterStudentRequest } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';
import { ToastMessageType } from '@k12/models';
import { RosterProgressStatuses } from '@k12/models';
import { ROSTER_CERTIFICATION_RESTRICTED_TOOLTIP } from '../restriction-tooltip-message';
import { rosterActionErrorMessage } from '../roster-action-error';
import { FormatDatePipe } from '@k12/pipes';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-certification-actions',
  templateUrl: './certification-actions.component.html',
  styleUrl: './certification-actions.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule, MatTooltipModule]
})
export class CertificationActionsComponent {
  @Input() student: SchoolRosterStudent | null = null;
  @Input() schoolCertificationRestricted: boolean | null = null;
  @Input() form!: FormGroup;
  @Input() set isNotAttending(value: boolean) {
    this._isNotAttending = value;
    this.certifyChoice = value ? 'no' : null;
  }
  get isNotAttending(): boolean {
    return this._isNotAttending;
  }
  @Input() individualCostErrors: string[] = [];
  @Output() certified = new EventEmitter<string>();
  @Output() validateForm = new EventEmitter<void>();
  @Output() syncFormToModel = new EventEmitter<void>();
  @Output() cancelForm = new EventEmitter<void>();

  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);

  private _isNotAttending = false;
  certifyChoice: 'yes' | 'no' | null = null;
  certifying = false;
  readonly restrictionTooltipMessage = ROSTER_CERTIFICATION_RESTRICTED_TOOLTIP;

  get isCertificationRestricted(): boolean {
    if (this.schoolCertificationRestricted !== null) {
      return this.schoolCertificationRestricted;
    }
    return this.student?.certificationRestricted === true;
  }

  onCancel(): void {
    this.certifyChoice = null;
    this.form.get('attendanceTypeId')?.markAsUntouched();
    this.form.get('attendanceTypeId')?.updateValueAndValidity();
    this.cancelForm.emit();
  }

  onCertifyChoiceSelect(choice: 'yes' | 'no'): void {
    if (this.isCertificationRestricted) return;
    this.certifyChoice = choice;
    // Trigger validation when selecting 'yes' for attending students
    if (choice === 'yes' && !this.isNotAttending) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.validateForm.emit();
    }
  }

  onCertify(): void {
    if (this.isCertificationRestricted) return;
    if (!this.canCertify()) return;
    this.certifying = true;
    this.syncFormToModel.emit();
    if (!this.student || !this.certifyChoice) return;
    const dto = this.createCertificationDto(this.certifyChoice);
    const successMessage =
      this.certifyChoice === 'yes' ? 'Student certified successfully' : 'Student marked as not certified';
    this.submitCertification(dto, successMessage);
  }

  private canCertify(): boolean {
    if (this.isCertificationRestricted) return false;
    if (this.certifying) return false;
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.validateForm.emit();
    if (this.form.invalid || this.individualCostErrors.length > 0 || !this.student || !this.certifyChoice) {
      this.toastMessageService.showToast(ToastMessageType.Error, 'Please fix validation errors before certifying');
      return false;
    }
    return true;
  }

  private createCertificationDto(certifyChoice: 'yes' | 'no'): UpdateSchoolRosterStudentRequest {
    if (!this.student) throw new Error('No student available');
    const localDateTime = this.getLocalISODateTime();

    // Build termPayments array from crossTermSummary
    // Exclude past terms that have no existing enrollment — they won't be created
    const termCosts = (this.student.crossTermSummary || [])
      .filter((term) => term.studentEnrollmentId || !FormatDatePipe.isBeforeToday(term.endDate))
      .map((term) => ({
        studentEnrollmentId: term.studentEnrollmentId || null,
        schoolTermGradeId: term.schoolTermGradeId,
        termCost: term.payment
      }));

    // Determine roster progress status based on certification choice
    const rosterProgressStatusId =
      certifyChoice === 'yes' ? RosterProgressStatuses.Certified : RosterProgressStatuses.Canceled;

    const dto: UpdateSchoolRosterStudentRequest = {
      studentEnrollmentId: this.student.studentEnrollmentId,
      studentId: this.student.studentId,
      schoolProgramPartnerId: this.student.schoolProgramPartnerId,
      schoolTermGradeId: this.student.schoolTermGradeId || '',
      attendanceTypeId: this.student.attendanceTypeId,
      rosterProgressStatusId: rosterProgressStatusId,
      academicYearStandardCostTotal: this.student.academicYearStandardCostTotal,
      academicYearIndividualCostTotal: this.student.academicYearIndividualCostTotal,
      termCost: this.getCurrentTermCost(),
      termCosts: termCosts,
      certificationDate: certifyChoice === 'yes' ? localDateTime : null,
      endorsedDate: this.student.endorsedDate || null,
      startDate: this.student.startDate,
      endDate: certifyChoice === 'no' ? localDateTime : null
    };

    return dto;
  }

  private submitCertification(dto: UpdateSchoolRosterStudentRequest, successMessage: string): void {
    this.schoolsService.updateSchoolRosterStudent(dto).subscribe({
      next: (response) => {
        this.certifying = false;
        if (response?.success) {
          if (this.student?.studentEnrollmentId) {
            this.certified.emit(this.student.studentEnrollmentId);
          }
          this.toastMessageService.showToast(ToastMessageType.Success, successMessage);
        } else {
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            response?.message || `Failed to ${successMessage.toLowerCase()}`
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.certifying = false;
        // The server re-checks the school's issue restrictions on submit, so a rejection here
        // carries the reason the button should have been disabled. Surface it verbatim.
        this.toastMessageService.showToast(
          ToastMessageType.Error,
          rosterActionErrorMessage(error, `Failed to ${successMessage.toLowerCase()}`)
        );
      }
    });
  }

  private getCurrentTermCost(): number | null | undefined {
    if (!this.student) return undefined;
    return (this.student.crossTermSummary || []).find((t) => t.schoolTermGradeId === this.student!.schoolTermGradeId)
      ?.payment;
  }

  private getLocalISODateTime(): string {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  }

  get isCertifyDisabled(): boolean {
    if (this.isCertificationRestricted) return true;
    if (this.isNotAttending) return !this.certifyChoice || this.certifying;
    return !this.certifyChoice || this.form.invalid || this.individualCostErrors.length > 0 || this.certifying;
  }
}
